import isString from 'lodash/isString';

export type AnswerFile = {
  name: string;
  url: string;
  mimeType?: string;
};

export type DocumentKind = 'image' | 'pdf' | 'word' | 'other';

// ---------------------------------------------------------------------------
// Canonical file-type constants — single source of truth shared with
// CandidateResume and all document-upload components.
// ---------------------------------------------------------------------------

export enum SupportedFileTypes {
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  PDF = 'application/pdf',
  PNG = 'image/png',
  JPG = 'image/jpg',
  JPEG = 'image/jpeg',
  DOC = 'application/msword',
}

export const FileTypeToExtension: { [key in SupportedFileTypes]: string } = {
  [SupportedFileTypes.PDF]: '.pdf',
  [SupportedFileTypes.DOC]: '.doc',
  [SupportedFileTypes.DOCX]: '.docx',
  [SupportedFileTypes.JPG]: '.jpg',
  [SupportedFileTypes.JPEG]: '.jpeg',
  [SupportedFileTypes.PNG]: '.png',
};

const IMAGE_MIMES: string[] = [SupportedFileTypes.PNG, SupportedFileTypes.JPG, SupportedFileTypes.JPEG];
const IMAGE_EXTS = ['jpg', 'jpeg', 'png'];
const PDF_MIME = SupportedFileTypes.PDF;
const WORD_MIMES: string[] = [SupportedFileTypes.DOC, SupportedFileTypes.DOCX];

export function normaliseAnswers(raw: any[] | null | undefined): AnswerFile[] {
  return (raw ?? []).map((a) => {
    if (isString(a)) {
      // Strip query string before extracting filename so Firebase Storage
      // URLs (e.g. ...jpg?alt=media&token=…) don't produce a token-hash extension
      const urlPath = a.split('?')[0];
      const rawName = urlPath.split('/').pop() ?? /* istanbul ignore next */ 'file';
      let name = rawName;
      try {
        name = decodeURIComponent(rawName);
      } catch {
        // keep rawName if decoding fails
      }
      return { name, url: a };
    }

    return a as AnswerFile;
  });
}

export function fileKind(file: AnswerFile): DocumentKind {
  const mime = file.mimeType?.toLowerCase() ?? '';
  // Get extension from name; fall back to URL path (before query string)
  const nameExt = file.name.split('.').pop()?.toLowerCase() ?? /* istanbul ignore next */ '';
  const urlExt = file.url.split('?')[0].split('/').pop()?.split('.')?.pop()?.toLowerCase() ?? /* istanbul ignore next */ '';
  const ext = nameExt || urlExt;
  if (IMAGE_MIMES.includes(mime) || mime.startsWith('image/') || IMAGE_EXTS.includes(ext)) return 'image';
  if (mime === PDF_MIME || ext === 'pdf') return 'pdf';
  if (WORD_MIMES.includes(mime) || ext === 'doc' || ext === 'docx') return 'word';
  return 'other';
}

export function getViewerUrl(file: AnswerFile): string {
  const mime = file.mimeType?.toLowerCase() ?? /* istanbul ignore next */ '';
  const name = file.name ?? /* istanbul ignore next */ '';
  const ext = name.includes('.') ? name.split('.').pop()?.toLowerCase() : /* istanbul ignore next */ '';
  const isPdf = mime === PDF_MIME || ext === 'pdf';
  if (isPdf) {
    return `${file.url}#toolbar=0`;
  }

  const isWord = WORD_MIMES.includes(mime) || ext === 'doc' || ext === 'docx';

  if (isWord) {
    return `https://docs.google.com/gview?url=${encodeURIComponent(file.url)}&embedded=true&view=preview`;
  }

  return file.url;
}
export const getDocumentFileName = (url: string) => {
  const decodedPath = decodeURIComponent(url.split('?')[0]);
  const pathFileName = decodedPath.split('/').pop() || '';
  const [, , , ...fileNameParts] = pathFileName.split('_');
  return fileNameParts.length ? fileNameParts.join('_') : pathFileName;
};
